// arabicAI.ts
// Mission 113 - في قلب الحدث - الذكاء الاصطناعي التوليدي
// Plateau TV arabe : débat sur les opportunités et risques de l'IA générative

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
} from './storyLoader';

export interface ArabicAIScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const arabicAICharacters: Character[] = [
  { id: 'leila',  name: 'ليلى الحسيني',   role: 'مقدمة البرنامج',           color: '#B45309', avatar: 'arabic_tv_leila_woman_30s' },
  { id: 'karim',  name: 'كريم المنصوري',  role: 'خبير تقنية المعلومات',      color: '#1E3A5F', avatar: 'arabic_tv_karim_man_40s'   },
  { id: 'omar',   name: 'عمر البكري',      role: 'خبير أخلاقيات الذكاء الاصطناعي', color: '#065F46', avatar: 'arabic_tv_omar_man_30s' },
];

// ─── Scène 1 ──────────────────────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'aai-scene-1',
  title: 'الحلقة الأولى : ثورة الذكاء الاصطناعي التوليدي',
  duration: 4,
  content: {
    narrativeText:
      "استوديو «في قلب الحدث» — شاشات رقمية تعرض إحصاءات: 300 مليون وظيفة مهددة بالذكاء الاصطناعي.\n\n" +
      "**ليلى :** «مرحباً بكم في حلقة اليوم حول الذكاء الاصطناعي التوليدي — التقنية التي هزّت أسس سوق العمل العالمي. ضيفنا الخبير سيشارك رؤيته معنا.»\n\n" +
      "**كريم :** «أنا متحمس جداً لهذه التقنية. ChatGPT، Gemini، Claude — هذه النماذج تُضاعف إنتاجية الموظفين بنسبة 40%.»\n\n" +
      "**اللاعب :** «الأرقام صحيحة، لكن علينا أن نكون دقيقين. الإنتاجية ترتفع في المهام التكرارية، أما المهام الإبداعية العميقة فلا تزال تحتاج العقل البشري.»\n\n" +
      "**عمر :** «لكن ماذا عن جودة المحتوى؟ رأيت شركات تنشر مقالات كاملة باستخدام الذكاء الاصطناعي دون مراجعة بشرية.»\n\n" +
      "**اللاعب :** «هذا خطأ استراتيجي. الذكاء الاصطناعي يُنتج محتوى واثقاً حتى حين يكون خاطئاً — نسمي هذا «الهلوسة» (hallucination). الإشراف البشري ضروري.»\n\n" +
      "**ليلى :** «إذن ما هو دورك الحقيقي كخبير في هذا المشهد؟»\n\n" +
      "**اللاعب :** «دوري هو مساعدة المؤسسات على بناء حوكمة للذكاء الاصطناعي — أي ضمان استخدامه بشكل مسؤول، شفاف، وخاضع للمراجعة البشرية.»\n\n" +
      "**كريم :** «حوكمة الذكاء الاصطناعي — هل هذا مجرد بيروقراطية تُبطئ الابتكار؟»\n\n" +
      "**اللاعب :** «بالعكس تماماً. الحوكمة الجيدة تبني الثقة مع العملاء والمنظمين، وهذه الثقة هي رأس مال لا يُشترى.»",
    characters: ['leila', 'karim', 'omar'],
  } as StoryContent,
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const card1: SequenceItem = {
  type: 'card',
  id: 'aai-card-1',
  title: 'مفاهيم الذكاء الاصطناعي التوليدي',
  duration: 5,
  content: {
    title: 'الذكاء الاصطناعي التوليدي : المفاهيم الأساسية',
    body:
      "## مفاهيم الذكاء الاصطناعي التوليدي\n\n" +
      "### ما هو الذكاء الاصطناعي التوليدي؟\n" +
      "تقنية تستخدم نماذج اللغة الكبيرة (LLMs) لإنتاج نصوص، صور، رموز برمجية، وصوت بناءً على أوامر نصية.\n\n" +
      "### النماذج الرئيسية\n" +
      "| النموذج | الشركة | الاستخدام الرئيسي |\n" +
      "|---|---|---|\n" +
      "| GPT-4 / ChatGPT | OpenAI | نصوص، كود، تحليل |\n" +
      "| Gemini | Google | متعدد الوسائط |\n" +
      "| Claude | Anthropic | حوار طويل، تحليل |\n" +
      "| Llama | Meta | مفتوح المصدر |\n\n" +
      "### الفرص في العالم العربي\n" +
      "- **التعليم** : أدوات تعلم شخصية باللغة العربية\n" +
      "- **الأعمال** : أتمتة التقارير والعروض التقديمية\n" +
      "- **الصحة** : مساعدة في التشخيص وتلخيص الملفات الطبية\n" +
      "- **القانون** : تحليل العقود والوثائق القانونية\n\n" +
      "### المخاطر والتحديات\n" +
      "- **الهلوسة (Hallucination)** : توليد معلومات خاطئة بثقة\n" +
      "- **التحيز (Bias)** : النماذج تعكس تحيزات بيانات التدريب\n" +
      "- **الملكية الفكرية** : قضايا حقوق النشر للمحتوى المولّد\n" +
      "- **الأمن السيبراني** : استخدام في إنتاج محتوى خادع (deepfakes)\n\n" +
      "### مبادئ الاستخدام المسؤول\n" +
      "١. الشفافية : الإفصاح عن استخدام الذكاء الاصطناعي\n" +
      "٢. الإشراف البشري : مراجعة كل مخرجات المخاطرة العالية\n" +
      "٣. الاختبار المستمر : رصد الانحرافات والأخطاء\n" +
      "٤. الخصوصية : عدم إدخال بيانات حساسة في الأنظمة العامة",
  } as CardContent,
};

// ─── Chat ─────────────────────────────────────────────────────────────────────

const chat1: SequenceItem = {
  type: 'chat',
  id: 'aai-chat-1',
  title: 'نقاش : تأثير الذكاء الاصطناعي على سوق العمل',
  duration: 6,
  content: {
    tutorCharacterId: 'karim',
    messages: [
      {
        id: 'aai-chat-q1',
        sender: 'character',
        characterId: 'karim',
        text: 'وفق تقارير McKinsey 2024، ما نسبة الوظائف المهددة بالأتمتة الكاملة بحلول 2030؟',
        type: 'single-choice',
        options: [
          'أقل من 5% من الوظائف',
          'بين 10% و15% من الوظائف',
          'بين 15% و30% من الوظائف',
          'أكثر من 60% من الوظائف',
        ],
        correctAnswer: 'بين 15% و30% من الوظائف',
        explanation: 'تُقدّر دراسات McKinsey أن ما بين 15% و30% من الأنشطة المهنية قابلة للأتمتة الكاملة بحلول 2030. لكن معظم الوظائف ستتغير جزئياً، لا أن تختفي كلياً.',
      },
      {
        id: 'aai-chat-q2',
        sender: 'character',
        characterId: 'karim',
        text: 'ما المهارات الأكثر مقاومة للأتمتة؟',
        type: 'single-choice',
        options: [
          'إدخال البيانات والتدقيق الإملائي',
          'التفكير النقدي والإبداع والتعاطف الاجتماعي',
          'الترجمة الآلية والتلخيص',
          'رصد الأنظمة والتحقق من الأخطاء',
        ],
        correctAnswer: 'التفكير النقدي والإبداع والتعاطف الاجتماعي',
        explanation: 'وفق المنتدى الاقتصادي العالمي (WEF 2025)، المهارات الأصعب أتمتةً هي: التفكير الإبداعي، الحل المعقد للمشكلات، الذكاء العاطفي، والتعاون البيني. هذه تُشكّل المزايا التنافسية للإنسان.',
      },
      {
        id: 'aai-chat-q3',
        sender: 'character',
        characterId: 'karim',
        text: 'ما المقصود بـ «الهلوسة» (Hallucination) في نماذج الذكاء الاصطناعي؟',
        type: 'single-choice',
        options: [
          'عندما يرفض النموذج الإجابة',
          'عندما يُنتج النموذج معلومات خاطئة بنبرة واثقة',
          'عندما تنتهي قدرة معالجة النموذج',
          'عندما يكرر النموذج نفس الإجابة',
        ],
        correctAnswer: 'عندما يُنتج النموذج معلومات خاطئة بنبرة واثقة',
        explanation: 'الهلوسة هي ظاهرة يُنتج فيها النموذج معلومات مختلقة (غير موجودة أو خاطئة) لكنه يُقدّمها بثقة تامة. هذا يجعل التحقق البشري من المخرجات أمراً ضرورياً، خاصة في المجالات الطبية والقانونية.',
      },
    ],
  } as ChatContent,
};

// ─── Quiz ─────────────────────────────────────────────────────────────────────

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'aai-quiz-1',
  title: 'اختبار : الذكاء الاصطناعي التوليدي',
  duration: 5,
  content: {
    questions: [
      {
        id: 'aai-q1-1',
        question: 'ما الفرق بين نموذج اللغة الكبير (LLM) والذكاء الاصطناعي التوليدي؟',
        type: 'single-choice',
        options: [
          'لا فرق، المصطلحان متطابقان',
          'LLM هو نوع من نماذج التوليد، والذكاء الاصطناعي التوليدي مفهوم أشمل يضم النصوص والصور والصوت',
          'LLM يختص بالترجمة فقط',
          'الذكاء الاصطناعي التوليدي يعني الروبوتات فقط',
        ],
        correctAnswer: 'LLM هو نوع من نماذج التوليد، والذكاء الاصطناعي التوليدي مفهوم أشمل يضم النصوص والصور والصوت',
        explanation: 'الذكاء الاصطناعي التوليدي (Generative AI) هو الفئة الأشمل التي تضم نماذج توليد النصوص (LLMs) والصور (Diffusion Models) والصوت والفيديو. LLMs مثل GPT هي نماذج متخصصة في النصوص.',
      },
      {
        id: 'aai-q1-2',
        question: 'لماذا تُعدّ حوكمة الذكاء الاصطناعي ضرورية للشركات؟',
        type: 'single-choice',
        options: [
          'فقط للامتثال التنظيمي',
          'لإبطاء الابتكار ومنع المنافسة',
          'لبناء ثقة العملاء وضمان الاستخدام المسؤول وتجنب المخاطر القانونية',
          'لأن الحكومات تفرضها دائماً',
        ],
        correctAnswer: 'لبناء ثقة العملاء وضمان الاستخدام المسؤول وتجنب المخاطر القانونية',
        explanation: 'حوكمة الذكاء الاصطناعي تحمي الشركة من ثلاثة مخاطر: قانونية (قانون الذكاء الاصطناعي الأوروبي)، سمعية (تسرب بيانات أو تحيز)، وتشغيلية (قرارات خاطئة مكلفة).',
      },
      {
        id: 'aai-q1-3',
        question: 'ما أفضل ممارسة عند استخدام ChatGPT لكتابة تقرير مهني؟',
        type: 'single-choice',
        options: [
          'نشر المخرج مباشرة دون أي تعديل',
          'استخدام المخرج كنقطة انطلاق، ثم التحقق من الوقائع ومراجعة المحتوى',
          'إدخال بيانات العملاء الحساسة في الرسالة لتحسين الدقة',
          'الاعتماد على النموذج لإجراء جميع الحسابات المالية',
        ],
        correctAnswer: 'استخدام المخرج كنقطة انطلاق، ثم التحقق من الوقائع ومراجعة المحتوى',
        explanation: 'أفضل ممارسة هي التعاون البشري-الآلي: يوفر الذكاء الاصطناعي مسودة أولية، ثم يتحقق الإنسان من الوقائع ويُضيف السياق والحكم المهني. لا تُدخل بيانات سرية في أنظمة عامة.',
      },
    ],
  } as QuizContent,
};

// ─── Chapitre ─────────────────────────────────────────────────────────────────

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'الذكاء الاصطناعي التوليدي',
  subtitle: 'Arabic — فرص وتحديات الذكاء الاصطناعي التوليدي',
  learningObjectives: [
    'فهم مفهوم الذكاء الاصطناعي التوليدي ونماذجه الرئيسية',
    'تحليل تأثيره على سوق العمل العربي',
    'التمييز بين الهلوسة والمعلومات الصحيحة',
    'تطبيق مبادئ الاستخدام المسؤول بالعربية',
  ],
  sequence: [scene1, card1, chat1, quiz1],
};

export const arabicAIScenario: ArabicAIScenario = {
  id: 'arabic-ai',
  title: 'في قلب الحدث - الذكاء الاصطناعي التوليدي',
  subtitle: 'L\'IA générative : opportunité et défi',
  certification: 'Arabic',
  characters: arabicAICharacters,
  chapters: [chapter1],
};

export default arabicAIScenario;
